import { type RetryStrategy } from "@/src/lib/retry";
import type { CcRunPayload } from "@/src/lib/workflows/steps/cloudcruise/cc-run-payload";
import type { StepOutput, StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { CloudCruiseRunType } from "@/src/models/cloudcruise-run";
import { V2WorkflowType } from "@/src/models/constants/workflow-types";
import { ErrorType } from "@/src/models/error-log";
import { File } from "@/src/models/file";
import { Group } from "@/src/models/group";
import { Patient } from "@/src/models/patient";
import { Section } from "@/src/models/section";
import { Task, TaskStatus } from "@/src/models/task";
import {
  ApprovalType,
  type AthenaCoverageConfirmationMetadata,
  type ReferralMatchMetadata,
  type TprReceivingAssignmentConfirmationMetadata,
  type TprReferralOpsMetadata,
} from "@/src/models/task-approval";

export enum StepKey {
  RESOLVE_REPLY_FAX = "resolve_reply_fax",
  BUILD_REPLY_FAX = "build_reply_fax",
  SEND_REPLY_FAX = "send_reply_fax",
  APEX_MODMED_ATTACH_DOC_TO_TASK = "apex_modmed_attach_doc_to_task",
  /** @deprecated superseded by FETCH_PATIENT_DETAILS; kept for parsing of historical task_step rows. */
  APPOINTMENT_FETCH = "appointment_fetch",
  /** @deprecated superseded by RESOLVE_ASSIGNEE; kept for parsing of historical task_step rows. */
  AUTONOMY_RESOLVE_ASSIGNEE = "autonomy_assignee_resolution",
  ATHENA_UPLOAD_MISSING_INFO_FAX = "athena_upload_missing_info_fax",
  BALANCE_ENCOUNTER_LOCATION = "balance_encounter_location",
  BALANCE_HOSPITAL_DOC_AUTO_DELETE = "balance_hospital_doc_auto_delete",
  BALANCE_MODMED_ATTACH_DOC_TO_TASK = "balance_modmed_attach_doc_to_task",
  BALANCE_MODMED_DOC_ASSOCIATED_TASK = "balance_modmed_doc_associated_task",
  BALANCE_MODMED_GENERAL_TASK_CREATE = "balance_modmed_general_task_create",
  BALANCE_MODMED_RECALL_CREATE = "balance_modmed_recall_create",
  BALANCE_MODMED_RECALL_SKIP_CHECK = "balance_modmed_recall_skip_check",
  BALANCE_TASK_SKIP_CHECK = "balance_task_skip_check",
  CC_METADATA_SETUP = "cc_metadata_setup",
  CC_METADATA_UPDATE = "cc_metadata_update",
  CC_PATIENT_METADATA_UPDATE = "cc_patient_metadata_update",
  CC_TE_CREATION = "cc_te_creation",
  CHECK_INSURANCE_ELIGIBILITY = "check_insurance_eligibility",
  CLINICIENT_AUTHORIZATION_DOCUMENT_UPLOAD = "clinicient_authorization_document_upload",
  CLINICIENT_AUTHORIZATION_TASK = "clinicient_authorization_task",
  CLINICIENT_CASE_DOCUMENT_UPLOAD = "clinicient_case_document_upload",
  CLINICIENT_CASE_TASK = "clinicient_case_task",
  CLINICIENT_DOCUMENT_NOTE = "clinicient_document_note",
  CLINICIENT_ENSURE_AUTHORIZATION = "clinicient_ensure_authorization",
  CLINICIENT_ENSURE_CASE = "clinicient_ensure_case",
  CLINICIENT_CONFIGURE_CASE = "clinicient_configure_case",
  CLINICIENT_SYNC_INSURANCE = "clinicient_sync_insurance",
  DETERMINE_PATIENT_TIER = "determine_patient_tier",
  SMS_ELIGIBILITY = "sms_eligibility",
  COBALT_APPOINTMENT_FETCH = "cobalt_appointment_fetch",
  COBALT_DOC_UPLOAD = "cobalt_doc_upload",
  COBALT_PATIENT_FETCH = "cobalt_patient_fetch",
  COBALT_STAFF_LIST = "cobalt_staff_list",
  COBALT_TEL_ENC = "cobalt_tel_enc",
  CREATE_COVERAGE = "create_coverage",
  CONFIRM_ATHENA_COVERAGE = "confirm_athena_coverage",
  CREATE_OR_UPDATE_PATIENT = "create_or_update_patient",
  CREATE_PATIENT = "create_patient",
  CREATE_REFERRAL = "create_referral",
  CREATE_PATIENT_CASE = "create_patient_case",
  CREATE_REFERRAL_AUTH_TASK = "create_referral_auth_task",
  CREATE_REFERRAL_AUTHORIZATION = "create_referral_authorization",
  DOC_ROUTING_EMAIL = "doc_routing_email",
  ENTP_DME_ENCOUNTER_CONTEXT = "entp_dme_encounter_context",
  ENTP_SLEEP_ENCOUNTER_CONTEXT = "entp_sleep_encounter_context",
  DOC_ROUTING_FAX = "doc_routing_fax",
  EHR_DOC_UPLOAD = "ehr_doc_upload",
  ENSURE_PATIENT = "ensure_patient",
  EXIT_IF_OPEN_REFERRAL = "exit_if_open_referral",
  FCC_CHART_UPSERT = "fcc_chart_upsert",
  FCC_CLINICAL_FORM = "fcc_clinical_form",
  FCC_DOC_UPLOAD = "fcc_doc_upload",
  FCC_REFERRAL_TRACKING_UPDATE = "fcc_referral_tracking_update",
  FCC_SPECIALTY_FORWARD = "fcc_specialty_forward",
  FCC_VA_MILITARY_FORWARD = "fcc_va_military_forward",
  FETCH_APPOINTMENT_PROVIDER = "fetch_appointment_provider",
  FETCH_PATIENT_DETAILS = "fetch_patient_details",
  FETCH_PCP = "fetch_pcp",
  SCHEDULING_RECOMMENDATION = "scheduling_recommendation",
  SECTION_UPLOAD = "section_upload",
  SUPPORTING_SECTIONS_UPLOAD = "supporting_sections_upload",
  SYNC_ATHENA_COVERAGES = "sync_athena_coverages",
  SYNC_COVERAGE = "sync_coverage",
  SYNC_PATIENT_APPOINTMENTS = "sync_patient_appointments",
  MEDENT_DOC_UPLOAD = "medent_doc_upload",
  MODMED_CLICK_CHECK_ELIGIBILITY = "modmed_click_check_eligibility",
  MODMED_API_TASK_CREATE = "modmed_api_task_create",
  MODMED_FETCH_NEXT_APPOINTMENT = "modmed_fetch_next_appointment",
  MODMED_OPEN_TASK_CHECK = "modmed_open_task_check",
  MODMED_PATIENT_TASK_CREATE = "modmed_patient_task_create",
  MODMED_REFERRING_PROVIDER_UPDATE = "modmed_referring_provider_update",
  NOTIFYMD_VALIDATE_PHI = "notifymd_validate_phi",
  ORDER_ATTACH = "order_attach",
  ORDER_MATCH = "order_match",
  ORDER_UPDATE = "order_update",
  OUT_OF_SCOPE_CHECK = "out_of_scope_check",
  PA_EAST_HOSPITALIZATION_HISTORY_UPDATE = "pa_east_hospitalization_history_update",
  // PA Central reuses these StepKey members too (see step-outputs.ts) — StepOutputs is scoped per
  // task, so there's no cross-tenant collision risk, and it lets Central call East's document-type
  // dispatch functions unmodified.
  PA_EAST_RESOLVE_ASSIGNEE = "pa_east_assignee_resolution",
  PA_EAST_ROUTING = "pa_east_routing",
  PA_EAST_WELL_CHILD_CHECK = "pa_east_well_child_check",
  PATIENT_NOT_IN_EHR_CHECK = "patient_not_in_ehr_check",
  INSURANCE_COMM_PATIENT_ROUTE = "insurance_comm_patient_route",
  PERSIST_TASK_METADATA = "persist_task_metadata",
  PERSIST_ROUTED_LOCATION = "persist_routed_location",
  PIMA_SCHEDULING_ELIGIBILITY_CHECK = "pima_scheduling_eligibility_check",
  PIMA_SCHEDULING_RECOMMENDATION = "pima_scheduling_recommendation",
  PREFLIGHT = "preflight",
  REFERRAL_DISPOSITION = "referral_disposition",
  REFERRAL_DOCUMENT_METADATA = "referral_document_metadata",
  REFERRAL_SHEET_EXPORT = "referral_sheet_export",
  REFERRAL_ATTACH = "referral_attach",
  REFERRAL_CLOSURE = "referral_closure",
  REFERRAL_MATCH = "referral_match",
  REFERRAL_OPEN_CHECK = "referral_open_check",
  REFERRAL_ORDER_CREATION = "referral_order_creation",
  REFERRALS_FETCH = "referrals_fetch",
  READ_ATHENA_COVERAGE = "read_athena_coverage",
  REQUIRE_ASSIGNEE = "require_assignee",
  REQUIRE_PATIENT_INFO = "require_patient_info",
  REQUIRE_PATIENT_MRN = "require_patient_mrn",
  REQUIRE_PATIENT_PHONE = "require_patient_phone",
  REQUIRE_PROVIDER = "require_provider",
  REQUIRE_DOCUMENT_SIGNATURE = "require_document_signature",
  REQUIRE_WEBECW_ASSIGNEE = "require_webecw_assignee",
  RESOLVE_ASSIGNEE = "resolve_assignee",
  RESOLVE_ORDERING_PROVIDER = "resolve_ordering_provider",
  RESOLVE_RECEIVING_ASSIGNMENT = "resolve_receiving_assignment",
  RESOLVE_TPR_INSURANCE_REQUIREMENTS = "resolve_tpr_insurance_requirements",
  SEND_REFERRAL_EMAIL = "send_referral_email",
  SET_TASK_PRIORITY = "set_task_priority",
  SFTP_DOC_UPLOAD = "sftp_doc_upload",
  SUBMIT_ATHENA_INSURANCE_ELIGIBILITY = "submit_athena_insurance_eligibility",
  SYNC_DOC_NAME = "sync_doc_name",
  MODMED_ATTACH_DOC_TO_TASK = "modmed_attach_doc_to_task",
  MODMED_PERFORMED_DATE_UPDATE = "modmed_performed_date_update",
  TCO_APEX_FORWARD = "tco_apex_forward",
  TPR_CAMELBACK_FOLLOW_THROUGH = "tpr_camelback_follow_through",
  TPR_CLOSE_DOCUMENT = "tpr_close_document",
  TPR_MERGE_ASSOCIATED_REFERRAL = "tpr_merge_associated_referral",
  TPR_PCP_REFERRAL_FAX = "tpr_pcp_referral_fax",
  TPR_REFERRAL_AUTH_REQUEST_CONFIRM = "tpr_referral_auth_request_confirm",
  TPR_REFERRAL_AUTH_REQUEST_UPLOAD = "tpr_referral_auth_request_upload",
  TPR_REFERRAL_AUTHORIZATION = "tpr_referral_authorization",
  TPR_REFERRAL_OPS_V2 = "tpr_referral_ops_v2",
  TPR_WAIT_FOR_ASSOCIATED_REFERRAL = "tpr_wait_for_associated_referral",
  TE_ATTACH = "te_attach",
  UPDATE_CARE_TEAM = "update_care_team",
  USRC_CPS_REFERRAL_TASK_LINK = "usrc_cps_referral_task_link",
  WAIT_UNTIL_NO_PENDING_REFERRALS = "wait_until_no_pending_referrals",
  WEBECW_ATTACH_DOC_TO_ENCOUNTER = "webecw_attach_doc_to_encounter",
  WEBECW_AUTO_ASSIGN = "webecw_auto_assign",
  WEBECW_COVERAGE = "webecw_coverage",
  WEBECW_DI_ORDER_UPDATE = "webecw_di_order_update",
  WEBECW_DOC_UPLOAD = "webecw_doc_upload",
  WEBECW_PROVIDER_CONTEXT = "webecw_provider_context",
  WEBECW_REFERRAL_ORDER = "webecw_referral_order",
  WEBECW_REFERRAL_PATIENT_CREATION = "webecw_referral_patient_creation",
  WEBECW_REFERRING_PROVIDER_UPDATE = "webecw_referring_provider_update",
  WEBECW_RENDERING_PROVIDER_UPDATE = "webecw_rendering_provider_update",
  WEBECW_TEL_ENC = "webecw_tel_enc",
}

export enum StepResultType {
  ASYNC = "async",
  COMPLETED = "completed",
  EARLY_EXIT = "early_exit",
  FAILED = "failed",
  /** Step is waiting on a timer; the step will retry on wake. */
  PAUSED = "paused",
  /** Step completed but requires human approval before the workflow advances. */
  PENDING_APPROVAL = "pending_approval",
}

export type ApprovalPayload =
  | {
      approvalType: ApprovalType.REFERRAL_MATCH;
      metadata: ReferralMatchMetadata;
    }
  | {
      approvalType: ApprovalType.ATHENA_COVERAGE;
      metadata: AthenaCoverageConfirmationMetadata;
    }
  | {
      approvalType: ApprovalType.TPR_RECEIVING_ASSIGNMENT;
      metadata: TprReceivingAssignmentConfirmationMetadata;
    }
  | {
      approvalType: ApprovalType.TPR_REFERRAL_OPS;
      metadata: TprReferralOpsMetadata;
    };

export type StepResult =
  | { type: StepResultType.COMPLETED; output?: StepOutput }
  | { type: StepResultType.ASYNC; output?: StepOutput }
  | { type: StepResultType.EARLY_EXIT; status: TaskStatus; output?: StepOutput }
  | { type: StepResultType.FAILED; errorType: ErrorType }
  | {
      type: StepResultType.PAUSED;
      /** Delay before the wake Cloud Task fires. */
      resumeAfterSeconds: number;
      /** Dedup key for the scheduled wake Cloud Task; bump for each new wake. */
      wakeKey: number;
      output?: StepOutput;
    }
  | { type: StepResultType.PENDING_APPROVAL; output?: StepOutput; approval: ApprovalPayload };

type StepContextBase = {
  task: Task;
  group: Group;
  section: Section;
  file: File;
  stepOutputs: StepOutputs;
  workflowType?: V2WorkflowType;
};

// A need is an effect that resolves a field the step declares and enforces its presence (throwing
// if absent). Steps declare their needs so patient-free steps carry no `patient` on their context —
// absence is a compile error, not a runtime convention. See src/lib/workflows/steps/define-step.ts.
export type Need<T> = (ctx: StepContextBase) => T | Promise<T>;
export type NeedRecord = Record<string, Need<unknown>>;

// Default N keeps `patient` on the context so existing patient-ful steps stay unchanged during the
// transition; patient-free steps opt out with `needs: {}` (StepContext<{}>).
export type StepContext<N extends NeedRecord = { patient: Need<Patient> }> = StepContextBase & {
  [K in keyof N]: Awaited<ReturnType<N[K]>>;
};

export type WorkflowStep = {
  key: StepKey;
  presentation?: { title: string; description: string };
  /** The CC run type this step owns. Must be unique across all steps in a workflow. */
  ccRunType?: CloudCruiseRunType;
  execute: (ctx: StepContext) => Promise<StepResult>;
  /** Extracts a step output from the CC run's success payload at webhook resume time. */
  extractOutput?(payload: CcRunPayload, prior: Record<string, unknown> | undefined): StepOutput | undefined;
  maxRetries?: number | RetryStrategy;
  errorType?: ErrorType;
  /**
   * Declares this step safe to delete-and-rerun from an AWAITING_INFORMATION exit (see
   * resumeAwaitingInformationTask in doc-management-workflow-v2.ts). Undefined/false by default —
   * only opt in for steps whose execute() is safe to restart from scratch with no prior output.
   */
  resumable?: boolean;
};

export const READ_STEP_MAX_RETRIES = 9;

// Idempotent writes (e.g. MRN-keyed createOrUpdatePatient) retry fewer times than reads: enough to
// ride out a transient EHR 5xx/throttle, not so many that a deterministic failure churns the queue.
export const WRITE_STEP_MAX_RETRIES = 3;

export const WEBECW_STEP_MAX_RETRIES = 9;

export function resolveStepRetryStrategy(step: WorkflowStep): RetryStrategy | undefined {
  if (step.maxRetries == null) return undefined;
  if (typeof step.maxRetries === "number") return { shouldRetry: () => true, maxRetries: step.maxRetries };

  return step.maxRetries;
}
